<template>
    <div ref="bldEnergyAnalysis" class="bld-energy-analysis">
        <!-- header -->
        <div class="analysis-header">에너지 운영 보고서</div>

        <!-- building info -->
        <div class="building-info-panel border">
            <BuildingInfoPanel
                ref="buildingInfoPanel"
                :buildingInfo="buildingInfo"
                @search="analysisReport"
                :activeTab="activeTab"
                :data="{
                    carbonData,
                    consumpData,
                    envData,
                }"
                @exportPDF="exportPDF"
                @updateTabSettings="updateTabSettings"
            />
        </div>

        <!-- tab area (터치: 좌/우 스와이프로 이전·다음 탭) -->
        <div
            class="tab-container overflowStyle"
            :class="{ 'tab-container--tab-swipe': tabSwipeEnabled }"
            @touchstart.passive="onTabSwipeTouchStart"
            @touchend.passive="onTabSwipeTouchEnd"
            @touchcancel.passive="onTabSwipeTouchCancel"
        >
            <!-- tab button -->
            <ul class="custom-tabs">
                <li
                    v-for="(tab, index) in visibleTabs"
                    :key="tab.key"
                    class="custom-tab"
                    :class="{ active: activeTab === index }"
                    @click="handleTabClick(index)"
                >
                    <span class="mr-1"><app-icon :icon="tab.icon" /></span>
                    <span>{{ tab.label }}</span>
                </li>

                <!-- auto play button -->
                <li
                    class="custom-tab auto-tab-button"
                    v-b-tooltip.hover
                    :title="autoTabActive ? '탭 자동 이동 중지' : '탭 자동 이동 시작'"
                    @click="toggleAutoTab"
                >
                    <i :class="autoTabActive ? 'fa fa-pause' : 'fa fa-play'" />
                </li>
            </ul>

            <!-- tab content -->
            <div class="tab-content-container border p-3 mb-0 overflowStyle">
                <transition name="slide-fade" mode="out-in">
                    <keep-alive>
                        <component :key="'tab-' + activeTab" :is="currentTab.component" v-bind="currentTabProps" />
                    </keep-alive>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import backEndApi from "@src/api/backEndApi";
import BuildingInfoPanel from "./panels/BuildingInfoPanel.vue";
import CarbonAnalysisPanel from "./panels/CarbonAnalysisPanel.vue";
import ConsumptionAnalysisPanel from "./panels/ConsumptionAnalysisPanel.vue";
import EnvironmentAnalysisPanel from "./panels/EnvironmentAnalysisPanel.vue";
import EnergySouceAnalysisPanel from "./panels/EnergySouceAnalysisPanel.vue";
import html2pdf from "html2pdf.js";
import { mergeGseedIntoBuildingInfo, mergeGseedIntoCarbonData } from "./data/gseedData";

export default {
    components: {
        BuildingInfoPanel,
        CarbonAnalysisPanel,
        ConsumptionAnalysisPanel,
        EnvironmentAnalysisPanel,
        EnergySouceAnalysisPanel,
    },
    data() {
        return {
            /* 건물 정보 */
            buildingInfo: null,

            /* 탭 상태 */
            activeTab: 0,
            autoTabActive: false,
            autoTabInterval: null,
            memoryMonitorInterval: null,

            /* 메모리 관리 */
            chartInstances: new Map(), // 차트 인스턴스 추적
            isTabTransitioning: false, // 탭 전환 중 플래그

            tabSwipeStartX: null,
            tabSwipeStartY: null,

            /* 탭 정의 */
            tabs: [
                {
                    key: "carbon",
                    label: "에너지 및 탄소 분석",
                    icon: "fa fa-cloud",
                    component: CarbonAnalysisPanel,
                    prop: "carbonData",
                    visible: true,
                },
                {
                    key: "consumption",
                    label: "소비 및 생산 분석",
                    icon: "fas fa-leaf",
                    component: ConsumptionAnalysisPanel,
                    prop: "consumpData",
                    visible: true,
                },
                {
                    key: "environment",
                    label: "운영 환경 분석",
                    icon: "fas fa-thermometer-half",
                    component: EnvironmentAnalysisPanel,
                    prop: "envData",
                    visible: true,
                },
                {
                    key: "cost",
                    label: "원단위 및 비용 분석",
                    icon: "fa fa-calculator",
                    component: EnergySouceAnalysisPanel,
                    prop: "costData",
                    visible: true,
                },
            ],

            /* 데이터 */
            carbonData: null,
            consumpData: null,
            envData: null,
            costData: null,
        };
    },

    computed: {
        /* visible 탭만 필터링 */
        visibleTabs() {
            return this.tabs.filter((tab) => tab.visible);
        },
        /* 현재 활성 탭 (visible 탭 중에서의 인덱스) */
        currentTab() {
            const visibleTab = this.visibleTabs[this.activeTab];
            if (!visibleTab) {
                // 활성 탭이 visible 탭 범위를 벗어나면 첫 번째 visible 탭 반환
                return this.visibleTabs[0] || null;
            }
            return visibleTab;
        },
        /* 동적으로 현재 패널에 필요한 prop 객체 생성 */
        currentTabProps() {
            if (!this.currentTab) return {};
            return {
                [this.currentTab.prop]: this[this.currentTab.prop],
            };
        },
        tabSwipeEnabled() {
            return this.visibleTabs.length > 1;
        },
    },

    created() {
        this.loadTabSettings();
        this.getSiteInfo();
    },

    beforeDestroy() {
        /* 자동 탭 타이머 제거 */
        clearInterval(this.autoTabInterval);
        clearInterval(this.memoryMonitorInterval);

        /* 모든 차트 인스턴스 정리 */
        this.cleanupAllCharts();
    },

    methods: {
        /* 탭 설정 로컬스토리지에서 로드 */
        loadTabSettings() {
            try {
                const savedSettings = localStorage.getItem("buildingEnergyEfficiencyTabSettings");
                if (savedSettings) {
                    const settings = JSON.parse(savedSettings);
                    this.tabs.forEach((tab) => {
                        if (settings.hasOwnProperty(tab.key)) {
                            tab.visible = settings[tab.key];
                        }
                    });
                    // 모든 탭이 비활성화되어 있으면 기본값으로 복원
                    if (!this.tabs.some((tab) => tab.visible)) {
                        this.tabs.forEach((tab) => {
                            tab.visible = true;
                        });
                    }
                    // 활성 탭이 보이지 않으면 첫 번째 visible 탭으로 변경
                    this.adjustActiveTab();
                }
            } catch (err) {
                console.error("탭 설정 로드 실패:", err);
            }
        },
        /* 탭 설정 저장 */
        saveTabSettings() {
            try {
                const settings = {};
                this.tabs.forEach((tab) => {
                    settings[tab.key] = tab.visible;
                });
                localStorage.setItem("buildingEnergyEfficiencyTabSettings", JSON.stringify(settings));
            } catch (err) {
                console.error("탭 설정 저장 실패:", err);
            }
        },
        /* 탭 설정 업데이트 */
        updateTabSettings(settings) {
            this.tabs.forEach((tab) => {
                if (settings.hasOwnProperty(tab.key)) {
                    tab.visible = settings[tab.key];
                }
            });
            // 모든 탭이 비활성화되어 있으면 기본값으로 복원
            if (!this.tabs.some((tab) => tab.visible)) {
                this.tabs.forEach((tab) => {
                    tab.visible = true;
                });
                this.$nextTick(() => {
                    this.$alertWarning("최소 1개 이상의 탭은 표시되어야 합니다.");
                });
            }
            this.saveTabSettings();
            this.adjustActiveTab();
        },
        /* 활성 탭 조정 (visible 탭 범위 내로) */
        adjustActiveTab() {
            this.$nextTick(() => {
                const visibleCount = this.visibleTabs.length;
                if (this.activeTab >= visibleCount && visibleCount > 0) {
                    this.activeTab = 0;
                }
            });
        },
        /* PDF 내보내기 */
        exportPDF() {
            const element = this.$refs.bldEnergyAnalysis;
            if (!element) return;

            element.classList.add("pdf-background");
            const opt = {
                margin: 10,
                filename: `건물에너지분석_${moment().format("YYYYMMDD_HHmmss")}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: [500, 297], orientation: "landscape" },
            };

            html2pdf()
                .set(opt)
                .from(element)
                .save()
                .finally(() => element.classList.remove("pdf-background"));
        },

        /* 탭 수동 클릭 */
        handleTabClick(idx) {
            if (this.activeTab === idx || this.isTabTransitioning) return;
            this.activeTab = idx;
            this.restartAutoTab();
        },

        onTabSwipeTouchStart(e) {
            if (!this.tabSwipeEnabled) return;
            if (e.target?.closest?.(".auto-tab-button")) return;
            const t = e.touches && e.touches[0];
            if (!t) return;
            this.tabSwipeStartX = t.clientX;
            this.tabSwipeStartY = t.clientY;
        },

        onTabSwipeTouchEnd(e) {
            if (!this.tabSwipeEnabled) return;
            const startX = this.tabSwipeStartX;
            const startY = this.tabSwipeStartY;
            this.tabSwipeStartX = null;
            this.tabSwipeStartY = null;
            if (startX == null || startY == null) return;
            if (this.isTabTransitioning) return;

            const t = e.changedTouches && e.changedTouches[0];
            if (!t) return;

            const dx = t.clientX - startX;
            const dy = t.clientY - startY;
            const threshold = 56;
            if (Math.abs(dx) < threshold) return;
            if (Math.abs(dx) <= Math.abs(dy)) return;

            const n = this.visibleTabs.length;
            if (n < 2) return;

            if (dx < 0) {
                this.handleTabClick((this.activeTab + 1) % n);
            } else {
                this.handleTabClick((this.activeTab - 1 + n) % n);
            }
        },

        onTabSwipeTouchCancel() {
            this.tabSwipeStartX = null;
            this.tabSwipeStartY = null;
        },

        /* 자동 탭 토글 */
        toggleAutoTab() {
            this.autoTabActive = !this.autoTabActive;
            this.restartAutoTab();

            // 메모리 모니터링 시작/중지
            if (this.autoTabActive) {
                this.memoryMonitorInterval = setInterval(this.monitorMemoryUsage, 5000);
            } else {
                clearInterval(this.memoryMonitorInterval);
            }
        },

        restartAutoTab() {
            clearInterval(this.autoTabInterval);
            if (this.autoTabActive) {
                this.autoTabInterval = setInterval(() => {
                    const visibleCount = this.visibleTabs.length;
                    if (visibleCount > 0) {
                        this.activeTab = (this.activeTab + 1) % visibleCount;
                    }
                }, 10000);
            }
        },

        /* 메모리 정리와 함께 탭 전환 */
        switchTabWithCleanup() {
            if (this.isTabTransitioning) return;

            this.isTabTransitioning = true;

            // 현재 탭의 차트들 정리
            this.cleanupCurrentTabCharts();

            // 다음 탭으로 이동
            this.activeTab = (this.activeTab + 1) % this.tabs.length;

            // 다음 tick에서 차트 초기화
            this.$nextTick(() => {
                this.isTabTransitioning = false;
            });
        },

        /* 현재 탭의 차트들 정리 */
        cleanupCurrentTabCharts() {
            const currentComponent = this.$children.find(
                (child) => child.$options.name === this.currentTab.component.name
            );

            if (currentComponent && typeof currentComponent.cleanupCharts === "function") {
                currentComponent.cleanupCharts();
            }
        },

        /* 모든 차트 정리 */
        cleanupAllCharts() {
            this.chartInstances.forEach((instance) => {
                if (instance && typeof instance.dispose === "function") {
                    instance.dispose();
                }
            });
            this.chartInstances.clear();
        },

        /* 메모리 사용량 모니터링 */
        monitorMemoryUsage() {
            if (performance.memory) {
                const memory = performance.memory;
                console.log("Memory Usage:", {
                    used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + " MB",
                    total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + " MB",
                    limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024) + " MB",
                });
            }
        },

        /* API */
        async getSiteInfo() {
            try {
                const { status, data } = await backEndApi.buildingEfficiency.getSiteInfo();
                if (status === 200) {
                    this.buildingInfo = mergeGseedIntoBuildingInfo(data);
                } else {
                    this.alertWarning(`${data.message}`);
                }
            } catch (err) {
                console.error("getSiteInfo 에러:", err);
            }
        },
        analysisReport(year, month) {
            const regDt = `${year || ""}${month || ""}` || moment().format("YYYY-MM");
            this.analyzeEnergyAndCarbon(regDt);
            this.analyzeEnergyFlow(regDt);
            this.analyzeOperatingEnvironment(regDt);
            this.analyzeEnergyUnitAndCost(regDt);
        },
        async analyzeEnergyAndCarbon(regDt) {
            try {
                const { status, data } = await backEndApi.buildingEfficiency.analyzeEnergyAndCarbon(regDt);
                if (status === 200) {
                    this.carbonData = mergeGseedIntoCarbonData(data);
                } else {
                    this.alertWarning(`${data.message}`);
                }
            } catch (err) {
                console.error("analyzeEnergyAndCarbon 에러:", err);
            }
        },
        async analyzeEnergyFlow(regDt) {
            try {
                const { status, data } = await backEndApi.buildingEfficiency.analyzeEnergyFlow(regDt);
                if (status === 200) {
                    this.consumpData = data;
                } else {
                    this.alertWarning(`${data.message}`);
                }
            } catch (err) {
                console.error("getSiteInfo 에러:", err);
            }
        },
        async analyzeOperatingEnvironment(regDt) {
            try {
                const { status, data } = await backEndApi.buildingEfficiency.analyzeOperatingEnvironment(regDt);
                if (status === 200) {
                    this.envData = data;
                } else {
                    this.alertWarning(`${data.message}`);
                }
            } catch (err) {
                console.error("getSiteInfo 에러:", err);
            }
        },
        async analyzeEnergyUnitAndCost(regDt) {
            try {
                const { status, data } = await backEndApi.buildingEfficiency.analyzeEnergyUnitAndCost(regDt);
                if (status === 200) {
                    this.costData = data;
                } else {
                    this.alertWarning(`${data.message}`);
                }
            } catch (err) {
                console.error("getSiteInfo 에러:", err);
            }
        },
    },
};
</script>

<style scoped>
.bld-energy-analysis {
    width: 100%;
    height: 91vh;
    display: flex;
    flex-direction: column;
}
/* header */
.analysis-header {
    background: #002060;
    color: #fff;
    width: fit-content;
    height: fit-content;
    font-size: 1rem;

    padding: 5px 10px;
    font-weight: bold;
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
}
/* info panel */
.building-info-panel {
    width: 100%;
    margin-bottom: 10px !important;
    background: #fff;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.24);
    border-radius: 0 10px 10px 10px;
    padding: 10px;
}
/* tabs */
.custom-tabs {
    display: flex;
    margin: 0;
    padding: 0;
    list-style: none;
}
.custom-tab {
    padding: 0.5rem 1.2rem;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    background: #f8f9fa;
    color: #495057;
    border-radius: 8px 8px 0 0;
    transition: background 0.2s;
    margin-right: 0.25rem;
}
.custom-tab:hover {
    background: #e2e6ea;
}
.custom-tab.active {
    background: #002060;
    color: #fff;
}
.auto-tab-button {
    margin-left: auto;
    background: #002060;
    color: #fff;
}
.auto-tab-button:hover {
    background-color: #002060;
}
/* tab container */
.tab-container {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.tab-container--tab-swipe {
    touch-action: pan-y;
}
.tab-content-container {
    flex: 1 1 auto;
    overflow: auto;
    overflow-x: hidden;

    background: #fff;
    border-radius: 0 10px 10px 10px;
    position: relative;
}

/* transition */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.5s ease;
    width: 100%;
}
.slide-fade-enter {
    transform: translateX(100%);
    opacity: 0;
}
.slide-fade-leave-to {
    transform: translateX(-100%);
    opacity: 0;
}

/* print */
.pdf-background {
    padding: 20px !important;
    box-sizing: border-box;
}
</style>
